import { create } from 'zustand';
import { type CredentialEditableField } from '../utils/chatEditableObjectUtils';
import {
  getResolvedObjectUpdateFields,
  markObjectUpdateFieldsResolved,
  markObjectUpdatesConsumed,
} from './consumedObjectUpdatesStore';

export type PendingCredentialFieldEdit = {
  field: CredentialEditableField;
  before: string | null;
  after: string | null;
  sourceUpdateId?: string;
};

export type PendingCredentialEdit = {
  fields: PendingCredentialFieldEdit[];
  reason?: string;
  timestamp: number;
};

type CredentialDraftFields = {
  role: string;
  roleId: string | null;
  roleDescription: string | null;
};

const CREDENTIAL_EDITABLE_FIELD_KEYS: CredentialEditableField[] = [
  'role',
  'roleId',
  'roleDescription',
];

function isCredentialEditableField(
  key: string,
): key is CredentialEditableField {
  return CREDENTIAL_EDITABLE_FIELD_KEYS.includes(
    key as CredentialEditableField,
  );
}

function normalizePatch(
  patch: Record<string, unknown>,
): Partial<CredentialDraftFields> {
  const normalized: Partial<CredentialDraftFields> = {};

  for (const key of CREDENTIAL_EDITABLE_FIELD_KEYS) {
    const value = patch[key];
    if (value === undefined) {
      continue;
    }
    if (key === 'roleId' || key === 'roleDescription') {
      normalized[key] = value === null ? null : String(value);
      continue;
    }
    normalized.role = typeof value === 'string' ? value : String(value);
  }

  return normalized;
}

function mergePendingFieldEdits({
  existing,
  patch,
  baseFields,
  sourceUpdateId,
}: {
  existing: PendingCredentialFieldEdit[];
  patch: Partial<CredentialDraftFields>;
  baseFields?: CredentialDraftFields;
  sourceUpdateId?: string;
}): PendingCredentialFieldEdit[] {
  const byField = new Map<CredentialEditableField, PendingCredentialFieldEdit>(
    existing.map((entry) => [entry.field, entry]),
  );

  for (const [key, after] of Object.entries(patch)) {
    if (!isCredentialEditableField(key) || after === undefined) {
      continue;
    }
    const before = baseFields?.[key] ?? null;
    if (before === after) {
      byField.delete(key);
      continue;
    }
    byField.set(key, {
      field: key,
      before,
      after,
      sourceUpdateId,
    });
  }

  return [...byField.values()];
}

function uniqueSourceUpdateIds(ids: Array<string | undefined>): string[] {
  return [
    ...new Set(
      ids.filter((id): id is string => typeof id === 'string' && id.length > 0),
    ),
  ];
}

function collectSourceUpdateIds(
  entries: PendingCredentialFieldEdit[],
): string[] {
  return uniqueSourceUpdateIds(entries.map((entry) => entry.sourceUpdateId));
}

function filterFullyResolvedSourceUpdateIds({
  resolvedEntries,
  remainingEntries,
}: {
  resolvedEntries: PendingCredentialFieldEdit[];
  remainingEntries: PendingCredentialFieldEdit[];
}): string[] {
  const resolvedSourceUpdateIds = collectSourceUpdateIds(resolvedEntries);
  const remainingSourceUpdateIds = new Set(
    collectSourceUpdateIds(remainingEntries),
  );
  return resolvedSourceUpdateIds.filter(
    (sourceUpdateId) => !remainingSourceUpdateIds.has(sourceUpdateId),
  );
}

type CredentialEditStore = {
  draftFieldsByCredentialId: Record<string, CredentialDraftFields>;
  pendingAiEdits: Record<string, PendingCredentialEdit>;
  stageAiPatch: (
    credentialId: string,
    {
      patch,
      reason,
      baseFields,
      sourceUpdateId,
    }: {
      patch: Record<string, unknown>;
      reason?: string;
      baseFields?: CredentialDraftFields;
      sourceUpdateId?: string;
    },
  ) => boolean;
  acceptAiEdits: (
    credentialId: string,
    fields?: CredentialEditableField[],
    options?: { baseDraftFields: CredentialDraftFields },
  ) => CredentialDraftFields | undefined;
  discardAiEdits: (
    credentialId: string,
    fields?: CredentialEditableField[],
  ) => void;
  getPendingAiEdits: (
    credentialId: string,
  ) => PendingCredentialEdit | undefined;
  getDraftFields: (credentialId: string) => CredentialDraftFields | undefined;
  syncCredentialEditBaseFields: (
    credentialId: string,
    baseFields: CredentialDraftFields,
  ) => void;
  acceptAllPendingAiEdits: (
    baseFieldsByCredentialId: Record<string, CredentialDraftFields>,
  ) => Record<string, CredentialDraftFields>;
  discardAllPendingAiEdits: () => void;
  clearPendingEditsForSourceUpdateIds: (sourceUpdateIds: string[]) => void;
  clearAllPendingCredentialEdits: () => void;
  clearCredentialEditState: (credentialId: string) => void;
};

export const useCredentialEditStore = create<CredentialEditStore>(
  (set, get) => ({
    draftFieldsByCredentialId: {},
    pendingAiEdits: {},

    stageAiPatch: (
      credentialId,
      { patch, reason, baseFields, sourceUpdateId },
    ) => {
      const normalizedPatch = normalizePatch(patch);
      if (sourceUpdateId) {
        const resolvedFields = getResolvedObjectUpdateFields(sourceUpdateId);
        for (const field of Object.keys(normalizedPatch)) {
          if (resolvedFields.has(field)) {
            delete normalizedPatch[field as CredentialEditableField];
          }
        }
      }

      if (Object.keys(normalizedPatch).length === 0) {
        return false;
      }

      const existingPending = get().pendingAiEdits[credentialId];
      const resolvedBaseFields =
        baseFields ?? get().draftFieldsByCredentialId[credentialId];
      const nextFields = mergePendingFieldEdits({
        existing: existingPending?.fields ?? [],
        patch: normalizedPatch,
        baseFields: resolvedBaseFields,
        sourceUpdateId,
      });

      if (nextFields.length === 0) {
        return false;
      }

      set((state) => ({
        pendingAiEdits: {
          ...state.pendingAiEdits,
          [credentialId]: {
            fields: nextFields,
            reason: reason ?? existingPending?.reason,
            timestamp: Date.now(),
          },
        },
        draftFieldsByCredentialId: resolvedBaseFields
          ? {
              ...state.draftFieldsByCredentialId,
              [credentialId]: resolvedBaseFields,
            }
          : state.draftFieldsByCredentialId,
      }));
      return true;
    },

    acceptAiEdits: (credentialId, fields, options) => {
      const pending = get().pendingAiEdits[credentialId];
      if (!pending || !options?.baseDraftFields) {
        return undefined;
      }

      const fieldsToAccept = fields
        ? pending.fields.filter((entry) => fields.includes(entry.field))
        : pending.fields;
      if (fieldsToAccept.length === 0) {
        return undefined;
      }

      const nextDraftFields = { ...options.baseDraftFields };
      for (const entry of fieldsToAccept) {
        if (entry.field === 'role') {
          nextDraftFields.role = entry.after ?? '';
          continue;
        }
        nextDraftFields[entry.field] = entry.after;
      }

      const remainingFields = pending.fields.filter(
        (entry) =>
          !fieldsToAccept.some((accepted) => accepted.field === entry.field),
      );

      const sourceUpdateIds = [
        ...new Set(
          fieldsToAccept
            .map((entry) => entry.sourceUpdateId)
            .filter(
              (id): id is string => typeof id === 'string' && id.length > 0,
            ),
        ),
      ];
      if (sourceUpdateIds.length > 0) {
        markObjectUpdatesConsumed(sourceUpdateIds);
        for (const sourceUpdateId of sourceUpdateIds) {
          markObjectUpdateFieldsResolved({
            sourceUpdateId,
            fields: fieldsToAccept
              .filter((entry) => entry.sourceUpdateId === sourceUpdateId)
              .map((entry) => entry.field),
          });
        }
      }

      set((state) => {
        const nextPendingAiEdits = { ...state.pendingAiEdits };
        if (remainingFields.length === 0) {
          delete nextPendingAiEdits[credentialId];
        } else {
          nextPendingAiEdits[credentialId] = {
            ...pending,
            fields: remainingFields,
          };
        }

        return {
          pendingAiEdits: nextPendingAiEdits,
          draftFieldsByCredentialId: {
            ...state.draftFieldsByCredentialId,
            [credentialId]: nextDraftFields,
          },
        };
      });

      return nextDraftFields;
    },

    discardAiEdits: (credentialId, fields) => {
      const pending = get().pendingAiEdits[credentialId];
      if (!pending) {
        return;
      }

      const discardedEntries = fields
        ? pending.fields.filter((entry) => fields.includes(entry.field))
        : pending.fields;

      const remainingFields = fields
        ? pending.fields.filter((entry) => !fields.includes(entry.field))
        : [];

      const consumedSourceUpdateIds = filterFullyResolvedSourceUpdateIds({
        resolvedEntries: discardedEntries,
        remainingEntries: remainingFields,
      });
      if (consumedSourceUpdateIds.length > 0) {
        markObjectUpdatesConsumed(consumedSourceUpdateIds);
      }

      set((state) => {
        const nextPendingAiEdits = { ...state.pendingAiEdits };
        if (!fields || remainingFields.length === 0) {
          delete nextPendingAiEdits[credentialId];
        } else {
          nextPendingAiEdits[credentialId] = {
            ...pending,
            fields: remainingFields,
          };
        }
        return { pendingAiEdits: nextPendingAiEdits };
      });
    },

    getPendingAiEdits: (credentialId) => get().pendingAiEdits[credentialId],

    getDraftFields: (credentialId) =>
      get().draftFieldsByCredentialId[credentialId],

    syncCredentialEditBaseFields: (credentialId, baseFields) => {
      const pending = get().pendingAiEdits[credentialId];
      const existingDraft = get().draftFieldsByCredentialId[credentialId];
      const draftUnchanged =
        existingDraft?.role === baseFields.role &&
        existingDraft?.roleId === baseFields.roleId &&
        existingDraft?.roleDescription === baseFields.roleDescription;

      if (!pending) {
        if (draftUnchanged || existingDraft) {
          return;
        }
        set((state) => ({
          draftFieldsByCredentialId: {
            ...state.draftFieldsByCredentialId,
            [credentialId]: baseFields,
          },
        }));
        return;
      }

      const refreshedFields = pending.fields
        .map((entry) => ({
          ...entry,
          before: baseFields[entry.field],
        }))
        .filter((entry) => entry.before !== entry.after);
      const pendingUnchanged =
        refreshedFields.length === pending.fields.length &&
        refreshedFields.every(
          (entry, index) =>
            entry.before === pending.fields[index]?.before &&
            entry.after === pending.fields[index]?.after &&
            entry.field === pending.fields[index]?.field,
        );

      if (pendingUnchanged && draftUnchanged) {
        return;
      }

      set((state) => {
        const nextPendingAiEdits = { ...state.pendingAiEdits };
        if (refreshedFields.length === 0) {
          delete nextPendingAiEdits[credentialId];
        } else {
          nextPendingAiEdits[credentialId] = {
            ...pending,
            fields: refreshedFields,
          };
        }

        return {
          pendingAiEdits: nextPendingAiEdits,
          draftFieldsByCredentialId: {
            ...state.draftFieldsByCredentialId,
            [credentialId]: baseFields,
          },
        };
      });
    },

    acceptAllPendingAiEdits: (baseFieldsByCredentialId) => {
      const acceptedByCredentialId: Record<string, CredentialDraftFields> = {};
      const credentialIds = Object.keys(get().pendingAiEdits).filter(
        (credentialId) =>
          (get().pendingAiEdits[credentialId]?.fields.length ?? 0) > 0,
      );

      for (const credentialId of credentialIds) {
        const baseDraftFields = baseFieldsByCredentialId[credentialId];
        if (!baseDraftFields) {
          continue;
        }
        const nextFields = get().acceptAiEdits(credentialId, undefined, {
          baseDraftFields,
        });
        if (nextFields) {
          acceptedByCredentialId[credentialId] = nextFields;
        }
      }

      return acceptedByCredentialId;
    },

    discardAllPendingAiEdits: () => {
      const consumedSourceUpdateIds = uniqueSourceUpdateIds(
        Object.values(get().pendingAiEdits).flatMap((pending) =>
          pending.fields.map((entry) => entry.sourceUpdateId),
        ),
      );
      if (consumedSourceUpdateIds.length > 0) {
        markObjectUpdatesConsumed(consumedSourceUpdateIds);
      }
      set({ pendingAiEdits: {} });
    },

    clearPendingEditsForSourceUpdateIds: (sourceUpdateIds) => {
      if (sourceUpdateIds.length === 0) {
        return;
      }

      const idsToClear = new Set(sourceUpdateIds);
      set((state) => {
        const nextPendingAiEdits = { ...state.pendingAiEdits };
        let changed = false;

        for (const [credentialId, pending] of Object.entries(
          nextPendingAiEdits,
        )) {
          const remainingFields = pending.fields.filter(
            (entry) =>
              !entry.sourceUpdateId || !idsToClear.has(entry.sourceUpdateId),
          );
          if (remainingFields.length === pending.fields.length) {
            continue;
          }

          changed = true;
          if (remainingFields.length === 0) {
            delete nextPendingAiEdits[credentialId];
          } else {
            nextPendingAiEdits[credentialId] = {
              ...pending,
              fields: remainingFields,
            };
          }
        }

        return changed ? { pendingAiEdits: nextPendingAiEdits } : state;
      });
    },

    clearAllPendingCredentialEdits: () => {
      set({ pendingAiEdits: {} });
    },

    clearCredentialEditState: (credentialId) => {
      set((state) => {
        const { [credentialId]: _draft, ...draftFieldsByCredentialId } =
          state.draftFieldsByCredentialId;
        const { [credentialId]: _pending, ...pendingAiEdits } =
          state.pendingAiEdits;
        return { draftFieldsByCredentialId, pendingAiEdits };
      });
    },
  }),
);
